import React, { useState, useEffect, useCallback } from 'react';
import {
  Badge,
  Button,
  Card,
  EditIcon,
  Modal,
  PublishRoute,
  Section,
  Spinner,
  Typography,
  WarningIcon,
  colorVars,
  motionVars,
  sizeVars,
} from '@routific/hive/v5';
import styled from 'styled-components';
import { observer } from 'mobx-react';
import { useTranslation } from 'react-i18next';
import { DeliveryMethod } from '../../store/mst/models';
import { useHistory } from 'react-router';
import { useStore } from '../../store/mst';
import { CompanySettingsViews } from '../../store/mst/Router/constants';
import { SettingsQueryParams } from '../../store/mst/Router/utils/SettingsPageQueryParams';
import { CustomerNotificationEvents } from '../../helpers/customerNotificationEvents';
import { getDeliveryScheduledLeadTimeLabel } from '../../helpers/deliveryScheduledLeadTime';
import { settingsPath } from '../../router/paths';

interface Props {
  visible: boolean;
  selectedRouteUuids: string[];
  onPublish: () => void;
  onClose: () => void;
}

// solid: cover the body with a mode-aware surface + spinner
// fading: opacity goes to 0 so the completed summary shows through
// hidden: cover unmounted after the fade finishes
type LoadingReveal = 'solid' | 'fading' | 'hidden';

// Matches Hive motionVars.duration.default (300ms) plus a small buffer so the
// timeout wins only when transitionend never fires (e.g. reduced-motion / 0ms).
const LOADING_COVER_FADE_FALLBACK_MS = 350;

// Figma sizes the modal illustration at 200px square. The Hive illustration
// fills its container, so the box is what sets the size.
const Illustration = styled.div`
  align-self: center;
  flex-shrink: 0;
  width: 200px;
  height: 200px;
`;

const IntroText = styled(Typography)`
  padding-left: ${sizeVars.inset.body.x};
`;

// Keep the full loaded layout mounted under the cover so the modal doesn't
// resize when the summary arrives. The cover is a solid canvas body fill
// (light or dark from the theme), then fades out to reveal the numbers.
const SummaryStack = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: ${sizeVars.gap.pair};
  width: 100%;
`;

const SummaryContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${sizeVars.gap.pair};
  width: 100%;
`;

const LoadingCover = styled.div<{ $opaque: boolean }>`
  position: absolute;
  inset: 0;
  z-index: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: ${colorVars.surface.canvas.body};
  opacity: ${({ $opaque }) => ($opaque ? 1 : 0)};
  transition: opacity ${motionVars.duration.default} ${motionVars.ease.default};
  pointer-events: ${({ $opaque }) => ($opaque ? 'auto' : 'none')};
`;

const NotificationColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
`;

const TimingColumn = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${sizeVars.gap.pair};
  width: 100%;
`;

const TimingContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  flex: 1;
  min-width: 0;
`;

const FooterActions = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: ${sizeVars.gap.raised};
`;

const PublishModal: React.FC<Props> = ({
  visible, selectedRouteUuids, onPublish, onClose,
}) => {
  // Start true so the first open paint is the full layout under a spinner,
  // not a flash of empty/zero summary content before the fetch effect runs.
  const [isLoadingSummary, setIsLoadingSummary] = useState<boolean>(true);
  const [loadingReveal, setLoadingReveal] = useState<LoadingReveal>('solid');
  const [unreachableDrivers, setUnreachableDrivers] = useState<number>(0);
  const [unreachableCustomers, setUnreachableCustomers] = useState<number>(0);
  const [unpublishedRoutesCount, setUnpublishedRoutesCount] = useState<number>(0);
  // Plain value from the summary response — keep Method + contact badge on the same fetch.
  const [summaryDeliveryMethod, setSummaryDeliveryMethod] = useState<string | null>(null);

  const store = useStore();
  const { customerNotification } = store;
  const { t } = useTranslation(['components', 'common', 'customerNotifications']);
  const history = useHistory();

  const deliveryMethod = customerNotification?.cnSettings?.deliveryMethod;
  const deliveryScheduled = customerNotification?.cnSettings?.deliveryScheduled;
  const deliveryScheduledThreshold = customerNotification?.cnSettings?.deliveryScheduledThreshold;

  // Prefer the method from the same summary that produced unreachableCustomers.
  const methodForDisplay = summaryDeliveryMethod ?? deliveryMethod;

  // Publish only triggers Delivery Scheduled CN — hide the card unless that event is on.
  const showDeliveryScheduledCard = methodForDisplay !== DeliveryMethod.NONE
    && Boolean(deliveryScheduled?.enabled);

  // Selection length holds the title/intro steady while the summary catches up.
  const displayRoutesCount = isLoadingSummary
    ? selectedRouteUuids.length
    : unpublishedRoutesCount;

  const showLoadingCover = loadingReveal !== 'hidden';
  const loadingCoverOpaque = loadingReveal === 'solid';

  const resetSummaryState = useCallback(() => {
    setUnreachableDrivers(0);
    setUnreachableCustomers(0);
    setUnpublishedRoutesCount(0);
    setSummaryDeliveryMethod(null);
    setIsLoadingSummary(true);
    setLoadingReveal('solid');
  }, []);

  // Stabilize selection identity — parents rebuild the array with .map() every render.
  const selectedRouteUuidsKey = selectedRouteUuids.join(',');

  // Refresh CN settings every open so Method isn't stuck on a pre-switch MST value.
  useEffect(() => {
    if (visible) {
      customerNotification.getCNSettings();
    }
  }, [visible, customerNotification]);

  useEffect(() => {
    if (!visible) {
      resetSummaryState();
      return undefined;
    }

    let cancelled = false;

    const loadSummary = async () => {
      setIsLoadingSummary(true);
      setLoadingReveal('solid');
      try {
        const summary = await store.model.getPublishSummary(selectedRouteUuids);
        if (cancelled) {
          return;
        }
        const publishingSummary = summary.planningNotifications.publishingSummary;
        const customerSummary = publishingSummary.customerNotifications;
        // Copy primitives immediately — CustomerNotificationsSummaryDto is an MST-GQL
        // typename singleton and must not be read later from a shared proxy.
        const nextUnreachableDrivers = publishingSummary.driverNotifications.unreachableDrivers ?? 0;
        const nextUnreachableCustomers = customerSummary?.unreachableCustomers ?? 0;
        const nextRoutesCount = publishingSummary.routesCount;
        const nextDeliveryMethod = customerSummary?.deliveryMethod ?? null;
        setUnreachableDrivers(nextUnreachableDrivers);
        setUnreachableCustomers(nextUnreachableCustomers);
        setUnpublishedRoutesCount(nextRoutesCount);
        setSummaryDeliveryMethod(nextDeliveryMethod);
      } catch (err) {
        if (!cancelled) {
          resetSummaryState();
        }
      } finally {
        if (!cancelled) {
          setIsLoadingSummary(false);
        }
      }
    };

    loadSummary();

    return () => {
      cancelled = true;
    };
  // Refetch when delivery method or selected routes change so contact checks stay in sync.
  // eslint-disable-next-line react-hooks/exhaustive-deps -- selectedRouteUuidsKey tracks content; store.model is stable
  }, [visible, deliveryMethod, selectedRouteUuidsKey, resetSummaryState]);

  // Once the summary is committed behind the solid cover, fade it so the
  // completed numbers show through, then unmount after the transition.
  useEffect(() => {
    if (!visible || isLoadingSummary || loadingReveal !== 'solid') {
      return undefined;
    }

    let frameTwo = 0;
    const frameOne = requestAnimationFrame(() => {
      frameTwo = requestAnimationFrame(() => {
        setLoadingReveal('fading');
      });
    });

    return () => {
      cancelAnimationFrame(frameOne);
      cancelAnimationFrame(frameTwo);
    };
  }, [visible, isLoadingSummary, loadingReveal]);

  // transitionend can miss (reduced-motion / duration 0). Always unmount.
  useEffect(() => {
    if (loadingReveal !== 'fading') {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      setLoadingReveal('hidden');
    }, LOADING_COVER_FADE_FALLBACK_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [loadingReveal]);

  const handleLoadingCoverTransitionEnd = (
    event: React.TransitionEvent<HTMLDivElement>,
  ) => {
    if (event.propertyName !== 'opacity' || loadingReveal !== 'fading') {
      return;
    }
    setLoadingReveal('hidden');
  };
  const navigateToCustomerNotifications = () => {
    history.push(`${settingsPath}?view=${CompanySettingsViews.CUSTOMER_NOTIFICATIONS}&${SettingsQueryParams.TEMPLATE}=${CustomerNotificationEvents.DELIVERY_SCHEDULED}`);
    onClose();
  };

  const getNotificationsTiming = () => {
    if (deliveryScheduledThreshold == null) {
      return t('publish.modal.confirm.notificationsTiming');
    }

    const { key, count } = getDeliveryScheduledLeadTimeLabel(deliveryScheduledThreshold);
    return t('publish.modal.confirm.notificationsTimingBeforeRouteStart', {
      time: t(`customerNotifications:${key}`, { count }),
    });
  };

  const getDeliveryMethodLabel = () => {
    if (methodForDisplay === DeliveryMethod.SMS) {
      return t('publish.modal.confirm.methodSms');
    }
    return t('publish.modal.confirm.methodEmail');
  };

  const getDriversMissingBadge = () => (
    <Badge variant="warning" size="md">
      <WarningIcon size="md" />
      {t('publish.modal.confirm.driversMissingBadge', { count: unreachableDrivers })}
    </Badge>
  );

  const getCustomersMissingContactBadge = () => (
    <Badge variant="warning" size="md">
      <WarningIcon size="md" />
      {t('publish.modal.confirm.cnMissingContactBadge', { count: unreachableCustomers })}
    </Badge>
  );

  const getNotificationDetailsColumns = () => (
    <Section.Columns align="start">
      <NotificationColumn>
        <Typography variant="key">{t('publish.modal.confirm.methodLabel')}</Typography>
        <Typography variant="body-strong">
          {getDeliveryMethodLabel()}
        </Typography>
      </NotificationColumn>
      <TimingColumn>
        <TimingContent>
          <Typography variant="key">{t('publish.modal.confirm.timingLabel')}</Typography>
          <Typography variant="body-strong">
            {getNotificationsTiming()}
          </Typography>
        </TimingContent>
        <Button
          hierarchy="tertiary"
          buttonType="icon-only"
          size="md"
          icon={<EditIcon size="md" />}
          aria-label={t('publish.modal.confirm.editTiming')}
          onClick={navigateToCustomerNotifications}
          data-cy="publish-modal-notifications-settings-btn"
          data-testid="publish-modal-notifications-settings-btn"
        />
      </TimingColumn>
    </Section.Columns>
  );

  const getDriversCard = () => (
    <Card
      header={t('publish.modal.confirm.driversCardTitle')}
      headerVariant="body-strong"
      headerAddon={!isLoadingSummary && unreachableDrivers > 0 ? getDriversMissingBadge() : undefined}
      data-testid="publish-modal-drivers-card"
    />
  );

  const getCustomerNotificationsCard = () => {
    if (!showDeliveryScheduledCard) {
      return null;
    }

    return (
      <Card
        header={t('publish.modal.confirm.cnCardTitle')}
        headerVariant="body-strong"
        headerAddon={!isLoadingSummary && unreachableCustomers > 0 ? getCustomersMissingContactBadge() : undefined}
        data-testid="publish-modal-cn-card"
      >
        {getNotificationDetailsColumns()}
      </Card>
    );
  };

  const getSummaryContent = () => (
    <SummaryStack aria-busy={showLoadingCover || undefined}>
      <SummaryContent>
        <Illustration>
          <PublishRoute />
        </Illustration>
        <IntroText variant="h5">
          {t('publish.modal.confirm.intro', { count: displayRoutesCount })}
        </IntroText>
        {getDriversCard()}
        {getCustomerNotificationsCard()}
      </SummaryContent>
      {showLoadingCover && (
        <LoadingCover
          $opaque={loadingCoverOpaque}
          data-testid="publish-modal-summary-loading"
          onTransitionEnd={handleLoadingCoverTransitionEnd}
        >
          <Spinner
            size="lg"
            status={t('publish.modal.confirm.summaryLoading')}
          />
        </LoadingCover>
      )}
    </SummaryStack>
  );

  const getFooter = () => (
    <FooterActions>
      <Button
        hierarchy="secondary"
        buttonType="text-only"
        size="md"
        onClick={onClose}
        data-cy="publish-modal-cancel-btn"
      >
        {t('common:cancel')}
      </Button>
      <Button
        hierarchy="primary"
        buttonType="text-only"
        size="md"
        onClick={onPublish}
        disabled={isLoadingSummary}
        data-cy="publish-modal-confirm-btn"
        data-testid="publish-modal-confirm-btn"
      >
        {t('publish.modal.confirm.button.text')}
      </Button>
    </FooterActions>
  );

  return (
    <Modal
      title={t('publish.modal.confirm.title', { count: displayRoutesCount })}
      open={visible}
      size="sm"
      onClose={onClose}
      footer={getFooter()}
      data-cy="publish-modal"
      data-testid="publish-modal"
    >
      {getSummaryContent()}
    </Modal>
  );
};

export default observer(PublishModal);
